/**
 * Add-to-Cart Restrictions UI Handler
 * Manages add-to-cart button states for product restrictions
 */

class ATCRestrictionsUI {
  constructor(restrictionsManager, productId, addToCartBtn) {
    this.restrictionsManager = restrictionsManager;
    this.productId = productId;
    this.addToCartBtn = addToCartBtn;
    this.currentVariantId = null;
  }

  /**
   * Sets the current variant ID
   * @param {number} variantId - Current variant ID
   */
  setCurrentVariantId(variantId) {
    this.currentVariantId = variantId;
  }

  /**
   * Updates button state based on cart data
   * @param {Object} cart - Cart data object
   */
  updateButtonState(cart) {
    if (!this.addToCartBtn) return;

    const shouldDisable = this.restrictionsManager.shouldDisableAddToCart(this.productId, cart);
    this.addToCartBtn.disabled = shouldDisable;
    
    if (shouldDisable) {
      this.addToCartBtn.setAttribute('disabled', 'disabled');
    } else {
      this.addToCartBtn.removeAttribute('disabled');
    }
  }

  /**
   * Handles cart reload when one-unit-only product is added
   * @param {Object} cart - Cart data object
   */
  handleOneUnitProductAdded(cart) {
    if (!this.restrictionsManager.isProductOneUnitOnly(this.productId) || !cart?.items?.length) {
      return;
    }
    
    const productInCart = cart.items.find(item => 
      item.product_id === this.productId
    );
    
    if (productInCart) {
      this.restrictionsManager.refreshCartData();
    }
  }

  /**
   * Initializes the UI handler with current cart state
   */
  async initialize() {
    try {
      const cart = await this.restrictionsManager.fetchCart();
      this.updateButtonState(cart);
    } catch (error) {
      console.error('Error initializing ATC restrictions UI:', error);
    }
  }

  /**
   * Sets up event listeners for cart changes
   */
  setupEventListeners() {
    // Handle cart changes
    document.addEventListener('rebuy:cart.change', (e) => {
      const cart = e.detail?.cart;
      this.updateButtonState(cart);
    });

    // Handle cart additions
    document.addEventListener('rebuy:cart.add', (e) => {
      const cart = e.detail?.cart;
      
      // Fetch canonical cart if data is invalid
      if (!cart?.items) {
        this.restrictionsManager.fetchCart()
          .then(canonicalCart => {
            this.updateButtonState(canonicalCart);
            this.handleOneUnitProductAdded(canonicalCart);
          });
        return;
      }
      
      this.updateButtonState(cart);
      this.handleOneUnitProductAdded(cart);
    });

    // Handle variant changes
    document.addEventListener('variant:change', (e) => {
      if (e.detail?.variant) {
        this.setCurrentVariantId(e.detail.variant.id);
        this.restrictionsManager.fetchCart()
          .then(cart => this.updateButtonState(cart));
      }
    });
  }
}

// Export the UI handler class
window.ATCRestrictionsUI = ATCRestrictionsUI; 